import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Check, X as XIcon, CalendarOff } from 'lucide-react';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/authStore';

const statusColor: Record<string, string> = { pending: 'warning', approved: 'success', rejected: 'danger' };

export default function Leave() {
  const { user } = useAuthStore();
  const isApprover = ['admin', 'registrar', 'superadmin'].includes(user?.role || '');
  const [tab, setTab] = useState<'mine' | 'team'>('mine');
  const [requests, setRequests] = useState<any[]>([]);
  const [balance, setBalance] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const doFetch = useCallback(async () => {
    setLoading(true);
    try {
      const [reqs, bal] = await Promise.all([
        api.get('/leave/requests', { params: tab === 'mine' ? { mine: 'true' } : {} }),
        tab === 'mine' ? api.get('/leave/balance') : Promise.resolve({ data: { data: [] } }),
      ]);
      setRequests(reqs.data.data || []);
      setBalance(bal.data.data || []);
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { doFetch(); }, [doFetch]);

  const decide = async (id: string, action: 'approve' | 'reject') => {
    if (action === 'reject' && !window.confirm('Reject this leave request?')) return;
    try {
      await api.patch(`/leave/requests/${id}/${action}`);
      toast.success(`Leave ${action}d`);
      doFetch();
    } catch (err: any) { toast.error(err?.response?.data?.message || 'Action failed'); }
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <div><h1 className="page-title">Leave</h1><p className="page-subtitle">Request and track staff leave</p></div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}><Plus size={16} />Request Leave</button>
      </div>

      {isApprover && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <button className="btn btn-sm" style={{ background: tab === 'mine' ? 'var(--primary)' : 'var(--surface)', color: tab === 'mine' ? '#fff' : 'var(--text)', border: '1px solid var(--border)' }} onClick={() => setTab('mine')}>My Leave</button>
          <button className="btn btn-sm" style={{ background: tab === 'team' ? 'var(--primary)' : 'var(--surface)', color: tab === 'team' ? '#fff' : 'var(--text)', border: '1px solid var(--border)' }} onClick={() => setTab('team')}>Team Requests</button>
        </div>
      )}

      {tab === 'mine' && balance.length > 0 && (
        <div className="grid-3" style={{ marginBottom: 20 }}>
          {balance.map((b: any) => (
            <div key={b.leave_type_id} className="stat-card">
              <div className="stat-label">{b.name}</div>
              <div className="stat-value" style={{ fontSize: 20 }}>{b.remaining} <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)' }}>/ {b.days_per_year} days left</span></div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="table-container">
          <table>
            <thead><tr>{tab === 'team' && <th>Staff</th>}<th>Type</th><th>Dates</th><th>Days</th><th>Reason</th><th>Status</th>{tab === 'team' && <th>Actions</th>}</tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={tab === 'team' ? 7 : 5} style={{ textAlign: 'center', padding: 40 }}><div className="loader loader-dark" style={{ margin: '0 auto' }} /></td></tr>
              : requests.length === 0 ? <tr><td colSpan={tab === 'team' ? 7 : 5}><div className="empty-state"><CalendarOff size={40} /><p>No leave requests</p></div></td></tr>
              : requests.map((r: any) => (
                <tr key={r.id}>
                  {tab === 'team' && <td style={{ fontWeight: 500 }}>{r.staff_first_name} {r.staff_last_name}</td>}
                  <td>{r.leave_type_name}</td>
                  <td style={{ fontSize: 12 }}>{new Date(r.start_date).toLocaleDateString()} – {new Date(r.end_date).toLocaleDateString()}</td>
                  <td>{r.days_requested}</td>
                  <td style={{ fontSize: 12, maxWidth: 200 }}>{r.reason || '—'}</td>
                  <td><span className={`badge badge-${statusColor[r.status]}`} style={{ textTransform: 'capitalize' }}>{r.status}</span></td>
                  {tab === 'team' && (
                    <td>
                      {r.status === 'pending' ? (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => decide(r.id, 'approve')}><Check size={13} />Approve</button>
                          <button className="btn btn-secondary btn-sm" onClick={() => decide(r.id, 'reject')}><XIcon size={13} />Reject</button>
                        </div>
                      ) : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.approved_by_first_name ? `${r.approved_by_first_name} ${r.approved_by_last_name}` : '—'}</span>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && <RequestLeaveModal onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); doFetch(); }} />}
    </div>
  );
}

function RequestLeaveModal({ onClose, onSaved }: any) {
  const [types, setTypes] = useState<any[]>([]);
  const [form, setForm] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  useEffect(() => { api.get('/leave/types').then(r => setTypes(r.data.data || [])); }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.leave_type_id || !form.start_date || !form.end_date) { toast.error('All fields except reason are required'); return; }
    setLoading(true);
    try {
      await api.post('/leave/requests', form);
      toast.success('Leave request submitted');
      onSaved();
    } catch (err: any) { toast.error(err?.response?.data?.message || 'Failed to submit'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>Request Leave</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}>×</button></div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">Leave Type *</label>
              <select className="form-input form-select" required value={form.leave_type_id || ''} onChange={e => set('leave_type_id', e.target.value)}>
                <option value="">Select...</option>
                {types.map((t: any) => <option key={t.id} value={t.id}>{t.name} ({t.days_per_year} days/yr)</option>)}
              </select>
            </div>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="form-group"><label className="form-label">Start Date *</label><input type="date" className="form-input" required value={form.start_date || ''} onChange={e => set('start_date', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">End Date *</label><input type="date" className="form-input" required value={form.end_date || ''} onChange={e => set('end_date', e.target.value)} /></div>
            </div>
            <div className="form-group"><label className="form-label">Reason</label><textarea className="form-input" rows={3} value={form.reason || ''} onChange={e => set('reason', e.target.value)} /></div>
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="loader" /> : 'Submit Request'}</button></div>
        </form>
      </div>
    </div>
  );
}
